import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Compass,
  Heart,
  Award,
  HandHelping,
  CalendarClock,
  Users,
  Check,
  ArrowRight,
} from "lucide-react";
import { useScrollFadeIn, fadeCls, fadeStyle } from "@/hooks/useScrollFadeIn";

/**
 * Diagnostic section — names the WHY behind long-term sick leave using the
 * 6-factor framework that is canonical in Nordic HR (NFA "guldkorn",
 * Arbetsmiljöverket, Arbeidstilsynet). Builds authority + qualifies Relark's
 * scope honestly: we help with 4 of 6, not all of them.
 */
const FACTORS = [
  { key: "influence", Icon: Compass, helped: true },
  { key: "meaning", Icon: Heart, helped: false },
  { key: "reward", Icon: Award, helped: false },
  { key: "support", Icon: HandHelping, helped: true },
  { key: "predictability", Icon: CalendarClock, helped: true },
  { key: "community", Icon: Users, helped: true },
] as const;

export default function RelarkSixFactors() {
  const { t } = useTranslation("ui");
  const { ref, isVisible } = useScrollFadeIn(0.1);

  return (
    <section className="border-t border-border/50 bg-muted/30 py-16 sm:py-24">
      <div ref={ref} className="mx-auto max-w-5xl px-5">
        <div className={`text-center max-w-2xl mx-auto ${fadeCls(isVisible)}`}>
          <p className="text-xs font-semibold uppercase tracking-wider text-primary mb-3">
            {t("relark.sixFactors.eyebrow")}
          </p>
          <h2 className="text-2xl font-bold sm:text-3xl">
            {t("relark.sixFactors.title")}
          </h2>
          <p className="mt-3 text-muted-foreground leading-relaxed">
            {t("relark.sixFactors.sub")}
          </p>
        </div>

        {/* Compact chip row — 6 ikoner i én strimmel, ingen desc */}
        <div className="mt-8 flex flex-wrap justify-center gap-2 sm:gap-3">
          {FACTORS.map(({ key, Icon, helped }, i) => (
            <div
              key={key}
              className={`inline-flex items-center gap-2 rounded-full border px-3 py-2 text-xs sm:text-sm ${
                helped
                  ? "border-primary/30 bg-primary/[0.06] text-foreground"
                  : "border-border bg-card text-muted-foreground"
              } ${fadeCls(isVisible)}`}
              style={fadeStyle(80 + i * 50)}
            >
              <Icon
                className={`h-3.5 w-3.5 ${
                  helped ? "text-primary" : "text-muted-foreground"
                }`}
              />
              <span className="font-medium">
                {t(`relark.sixFactors.factors.${key}.title`)}
              </span>
              {helped && (
                <Check className="h-3 w-3 text-primary" strokeWidth={3} />
              )}
            </div>
          ))}
        </div>

        <div
          className={`mt-8 rounded-2xl border-2 border-primary/25 bg-primary/[0.05] p-5 sm:p-6 text-center ${fadeCls(
            isVisible,
          )}`}
          style={fadeStyle(560)}
        >
          <p className="text-sm sm:text-base text-foreground/90 leading-relaxed">
            <span className="font-semibold text-primary">
              {t("relark.sixFactors.calloutLead")}
            </span>{" "}
            {t("relark.sixFactors.calloutBody")}
          </p>
          <p className="mt-2 text-xs text-muted-foreground">
            {t("relark.sixFactors.source")}
          </p>
          <Link
            to="/science"
            className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline underline-offset-4"
          >
            {t("relark.sixFactors.readMore")}
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>
    </section>
  );
}
